import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Send } from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import emailjs from "@emailjs/browser";

// Initialize EmailJS
emailjs.init("ns8iBr8YNGxt5OpPN");

// Register GSAP plugins
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

const CallBackForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    course: "",
    country: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const descRef = useRef(null);
  const formRef = useRef(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // 🔥 SEND CALLBACK REQUEST EMAIL
  const sendCallbackEmail = async () => {
    try {
      const fullEmailHTML = `
        <div style="font-family: system-ui, sans-serif; font-size:15px; color:#333; max-width:650px; margin:0 auto; background:#f9fff9; padding:30px; border-radius:15px; border:2px solid #0b9949;">

          <!-- LOGO -->
          <div style="text-align:center; margin-bottom:25px;">
            <div style="background:#0b9949; padding:20px; border-radius:8px;">
              <h1 style="color:white; margin:0; font-size:28px;">GREENMARK</h1>
              <p style="color:white; margin:5px 0 0 0; font-size:12px;">Training Institute</p>
            </div>
          </div>

          <!-- GREETING -->
          <p style="font-size:18px; margin-bottom:8px;">Hi <strong>GreenMark Team</strong>,</p>
          <p style="margin:0;">You have received a new callback request through the website.</p>

          <!-- CALLBACK REQUEST SUMMARY -->
          <div style="background:white; padding:22px; border-radius:12px; margin:30px 0; box-shadow:0 4px 14px rgba(0,0,0,0.12); border-left:6px solid #0b9949;">
            <h3 style="color:#0b9949; margin-top:0; margin-bottom:15px;">📞 New Callback Request</h3>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
              <p style="margin:8px 0;"><strong>Name:</strong> ${formData.name}</p>
              <p style="margin:8px 0;"><strong>Email:</strong> ${formData.email}</p>
              <p style="margin:8px 0;"><strong>Mobile:</strong> ${formData.mobile}</p>
              <p style="margin:8px 0;"><strong>Country:</strong> ${formData.country || "N/A"}</p>
              <p style="margin:8px 0; grid-column: 1 / -1;"><strong>Interested Course:</strong> ${formData.course || "Not specified"}</p>
            </div>
          </div>

          <!-- MESSAGE -->
          ${formData.message ? `
          <h3 style="color:#0b9949; margin-top:25px;">💬 Additional Message</h3>
          <div style="background:#f8f9fa; padding:15px; border-radius:8px; border:1px solid #e9ecef; margin-top:10px;">
            <p style="margin:0; line-height:1.6; white-space: pre-wrap;">${formData.message}</p>
          </div>
          ` : ''}

          <!-- ACTION REQUIRED -->
          <div style="background:#fff3cd; border:1px solid #ffeaa7; border-radius:8px; padding:15px; margin-top:25px;">
            <h4 style="color:#856404; margin:0 0 10px 0;">⏰ Action Required</h4>
            <p style="color:#856404; margin:0; font-size:14px;">
              Please contact ${formData.name} at ${formData.mobile} or ${formData.email} to discuss their interest in ${formData.course || "our courses"}.
            </p>
          </div>

          <!-- FOOTER -->
          <p style="margin-top:35px; text-align:center; font-size:14px; color:#666;">
            Request received on: <strong>${new Date().toLocaleDateString("en-GB")} at ${new Date().toLocaleTimeString("en-GB")}</strong><br><br>
            This callback request was submitted through the GreenMark website.
          </p>

        </div>
      `;

      console.log("📧 Sending callback request email...");

      // Send email using EmailJS
      const response = await emailjs.send(
        "service_7pytgxf",
        "template_lbjy8n3",
        {
          message_html: fullEmailHTML,
          to_email: "pcm@greenmark.com.sg", // Replace with actual Greensafe email
          reply_to: formData.email || "noreply@greensafe.sg",
          subject: `New Callback Request: ${formData.name} - ${formData.course || "Course Inquiry"}`
        }
      );

      console.log("✅ Callback email sent successfully:", response);
      return true;
    } catch (error) {
      console.error("❌ Callback email sending failed:", error);
      throw error;
    }
  };

  // 🔥 SUCCESS MODAL COMPONENT
  const SuccessModal = ({ show, onClose }) => {
    if (!show) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6 text-center">
          <div className="text-6xl mb-4">📞</div>
          <h2 className="text-2xl font-bold text-green-600 mb-4">Callback Request Sent!</h2>
          <p className="text-gray-700 mb-2">
            <strong>Thank you, {formData.name}!</strong>
          </p>
          <p className="text-gray-600 mb-4">
            Our team will contact you within 2 hours at <strong>{formData.mobile}</strong>.
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 text-sm text-green-700">
            <p><strong>What to expect:</strong></p>
            <p>• Call from our course consultant</p>
            <p>• Detailed course information</p>
            <p>• Schedule and pricing details</p>
            <p>• Any questions answered</p>
          </div>
          <button
            onClick={onClose}
            className="bg-[#0b9949] text-white px-6 py-2 rounded-md hover:bg-[#087c3a] transition-all w-full"
          >
            Got It!
          </button>
        </div>
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.email || !formData.mobile) {
      alert("❌ Please fill in at least your name, email, and mobile number!");
      return;
    }

    setIsSubmitting(true);

    try {
      console.log("📧 Sending callback request...");
      // Send callback email
      await sendCallbackEmail();

      // Show success modal
      setShowSuccessModal(true);

      // Reset form
      setFormData({
        name: "",
        email: "",
        mobile: "",
        course: "",
        country: "",
        message: "",
      });

    } catch (error) {
      console.error("❌ Callback request error:", error);
      alert(`❌ Error: ${error.message || 'Failed to send request. Please try again or call us directly.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setShowSuccessModal(false);
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate title
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: titleRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Animate description
      gsap.fromTo(
        descRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          delay: 0.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: descRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Animate form
      gsap.fromTo(
        formRef.current,
        { opacity: 0, y: 50, scale: 0.95 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1,
          delay: 0.4,
          ease: "power2.out",
          scrollTrigger: {
            trigger: formRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <>
      <section ref={sectionRef} className="py-24 px-6 md:px-16 bg-gradient-to-br from-[#f6fdf9] to-[#e9f9ef] relative overflow-hidden">
        {/* Decorative background image with light overlay */}
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1542744094-24638eff58bb?auto=format&fit=crop&w=1600&q=80')] bg-cover bg-center opacity-10"></div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.h2
            ref={titleRef}
            className="text-4xl font-bold text-[#0b9949] mb-4"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Request a Call Back
          </motion.h2>
          <motion.p
            ref={descRef}
            className="text-gray-700 max-w-2xl mx-auto mb-12 text-lg"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.7 }}
          >
            Have questions about our safety courses or consulting services?
            Fill in the form below and our experts will reach out to you shortly.
          </motion.p>

          <motion.form
            ref={formRef}
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-8 rounded-2xl shadow-lg backdrop-blur-md"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <input
              type="text"
              name="name"
              placeholder="Full Name *"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-[#0b9949] outline-none"
            />
            <input
              type="email"
              name="email"
              placeholder="Email Address *"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-[#0b9949] outline-none"
            />
            <input
              type="text"
              name="mobile"
              placeholder="Mobile Number *"
              value={formData.mobile}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-[#0b9949] outline-none"
            />
            <input
              type="text"
              name="course"
              placeholder="Interested Course"
              value={formData.course}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-[#0b9949] outline-none"
            />
            <input
              type="text"
              name="country"
              placeholder="Country"
              value={formData.country}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-[#0b9949] outline-none"
            />
            <textarea
              name="message"
              placeholder="Any specific questions or requirements?"
              rows="4"
              value={formData.message}
              onChange={handleChange}
              className="w-full md:col-span-2 border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-[#0b9949] outline-none"
            ></textarea>

            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              className="md:col-span-2 bg-[#0b9949] text-white flex items-center justify-center gap-2 py-3 rounded-full text-lg font-medium hover:bg-[#097c3c] transition-all disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                "⏳ Sending..."
              ) : (
                <>
                  <Send size={20} /> Submit Request
                </>
              )}
            </motion.button>

            <p className="md:col-span-2 text-xs text-gray-500 text-center">
              * Required fields. We'll contact you within 2 hours during business hours.
            </p>
          </motion.form>
        </div>
      </section>

      {/* Success Modal */}
      <SuccessModal show={showSuccessModal} onClose={handleModalClose} />
    </>
  );
};

export default CallBackForm;